import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { galleryPhotos, stagger } from '../utils/quinn';
import { useIsEditing, useRawProperty } from '../utils/hooks';
import { PATHS, useSiteFlags } from '../utils/site';

interface Photo {
  image?: string;
  alt_text?: string;
  description?: string;
}

const highlight1Def: SlotGroupDefinition = {
  label: 'Highlight 1',
  fields: {
    title: { label: 'Title', type: 'text', defaultValue: 'Unwind at home' },
    description: { label: 'Description', type: 'text', defaultValue: 'A resort-style pool with tanning ledges, and an outdoor lounge with a fireplace for the evenings.' },
  },
  generator: {
    prompt: 'A two- or three-word title and one sentence on how residents relax here, naming only amenities this property really has (pool, lounge, fireplace).',
    context: 'Highlight 1 of 3 in the Lifestyle Highlights list on the Why Quinn page, beside a photo of that amenity. Different amenities from highlights 2 and 3.',
  },
};

const highlight2Def: SlotGroupDefinition = {
  label: 'Highlight 2',
  fields: {
    title: { label: 'Title', type: 'text', defaultValue: 'Get together' },
    description: { label: 'Description', type: 'text', defaultValue: 'An outdoor kitchen with grills and bar seating, a fire pit, and cornhole on the lawn.' },
  },
  generator: {
    prompt: 'A two- or three-word title and one sentence on where neighbors gather here, naming only amenities this property really has (outdoor kitchen, grills, fire pit, lawn games).',
    context: 'Highlight 2 of 3 in the Lifestyle Highlights list on the Why Quinn page. Different amenities from highlights 1 and 3.',
  },
};

const highlight3Def: SlotGroupDefinition = {
  label: 'Highlight 3',
  fields: {
    title: { label: 'Title', type: 'text', defaultValue: 'Work from home' },
    description: { label: 'Description', type: 'text', defaultValue: 'A community lounge with a coffee bar, a few steps from your front door.' },
  },
  generator: {
    prompt: 'A two- or three-word title and one sentence on a place to work or focus here, naming only amenities this property really has (lounge, coffee bar, co-working).',
    context: 'Highlight 3 of 3 in the Lifestyle Highlights list on the Why Quinn page. Different amenities from highlights 1 and 2.',
  },
};

const photo1Def: SlotGroupDefinition = {
  label: 'Photo 1 (large)',
  description: 'Until a photo is picked, shows a gallery photo of the pool.',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[0].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[0].alt_text', defaultValue: '' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 0, fieldMappings: { image: 'image', alt: 'alt_text' } },
};

const photo2Def: SlotGroupDefinition = {
  label: 'Photo 2',
  description: 'Until a photo is picked, shows a gallery photo of the outdoor kitchen or grills.',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[1].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[1].alt_text', defaultValue: '' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 1, fieldMappings: { image: 'image', alt: 'alt_text' } },
};

const photo3Def: SlotGroupDefinition = {
  label: 'Photo 3',
  description: 'Until a photo is picked, shows a gallery photo of the community lounge or coffee bar.',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[2].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[2].alt_text', defaultValue: '' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 2, fieldMappings: { image: 'image', alt: 'alt_text' } },
};

const ctaDef: SlotGroupDefinition = {
  label: 'Button',
  description: 'Hidden while it points at the Amenities page and that page is hidden for the property.',
  fields: {
    label: { label: 'Button Text', type: 'text', defaultValue: 'See Every Amenity' },
    url: { label: 'URL', type: 'url', defaultValue: '/amenities' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

/** One gallery photo per highlight, matched on alt text first, then description. */
const KEYWORDS: RegExp[] = [/\bpool\b|\bswim/i, /outdoor kitchen|\bgrill/i, /lounge|coffee/i];

function keywordPicks(photos: Photo[]): Array<Photo | null> {
  const used = new Set<Photo>();
  return KEYWORDS.map((rx) => {
    const g =
      photos.find((x) => !used.has(x) && rx.test(x.alt_text || '')) ||
      photos.find((x) => !used.has(x) && rx.test(x.description || ''));
    if (g) used.add(g);
    return g || null;
  });
}

const item = 'flex flex-col gap-1.5 border-t border-[--q-border] py-5 last:border-b min-[768px]:max-[1199px]:py-[18px] max-[767px]:py-4';
const itemTitle = 'm-0 font-heading text-[24px] font-normal leading-[30px] max-[767px]:text-[22px] max-[767px]:leading-7';
const itemDesc = 'm-0 text-[15px] leading-6 text-[--q-muted] max-[767px]:leading-[22px]';
const tile = 'photo [&.photo]:bg-[--q-border]';
const tileLarge = 'h-[400px] max-[1199px]:h-[420px] max-[767px]:h-[240px]';
const tileSmall = 'h-[210px] max-[1199px]:h-[240px] max-[767px]:h-[130px]';

export function LifestyleHighlights() {
  const p = useRawProperty();
  const flags = useSiteFlags();
  const editing = useIsEditing();
  const h1 = useSlotGroup('lifestylehighlights.highlight_1', highlight1Def);
  const h2 = useSlotGroup('lifestylehighlights.highlight_2', highlight2Def);
  const h3 = useSlotGroup('lifestylehighlights.highlight_3', highlight3Def);
  const slots = [
    useSlotGroup('lifestylehighlights.photo_1', photo1Def),
    useSlotGroup('lifestylehighlights.photo_2', photo2Def),
    useSlotGroup('lifestylehighlights.photo_3', photo3Def),
  ];
  const cta = useSlotGroup('lifestylehighlights.cta', ctaDef);

  const photos = galleryPhotos<Photo>(p.photo_gallery);
  const picks = keywordPicks(photos);
  const tiles = slots.map((s, i) => {
    const overridden = !!s.image && s.image !== photos[i]?.image;
    if (overridden) return { src: s.image!, alt: s.alt || '' };
    const g = picks[i];
    return g?.image ? { src: g.image, alt: g.alt_text || '' } : null;
  });
  const found = tiles.filter(Boolean).length;
  // Fewer than three photos: one large photo, then none.
  const mode = editing || found === 3 ? 'mosaic' : found > 0 ? 'single' : 'none';
  const singleIndex = tiles.findIndex(Boolean);

  const ctaHref = cta.url || PATHS.amenities;
  const showCta = ctaHref !== PATHS.amenities || flags.shows('amenities');

  const tileBody = (i: number, large: boolean) => {
    const t = tiles[i];
    if (!t) return <span className="flex h-full items-center justify-center border border-dashed border-[--q-border] bg-[--q-surface] text-sm text-[--q-muted]">Photo {i + 1}</span>;
    return <VibesitesImage url={t.src} alt={t.alt} sizes={large ? '(min-width: 1200px) 41vw, 100vw' : '(min-width: 1200px) 20vw, 50vw'} />;
  };
  const photoTile = (i: number, large: boolean) => {
    const className = `${tile} ${large ? tileLarge : tileSmall}`;
    if (i === 0)
      return (
        <EditableSlotGroup groupPath="lifestylehighlights.photo_1" definition={photo1Def} className={className}>
          {tileBody(0, large)}
        </EditableSlotGroup>
      );
    if (i === 1)
      return (
        <EditableSlotGroup groupPath="lifestylehighlights.photo_2" definition={photo2Def} className={className}>
          {tileBody(1, large)}
        </EditableSlotGroup>
      );
    return (
      <EditableSlotGroup groupPath="lifestylehighlights.photo_3" definition={photo3Def} className={className}>
        {tileBody(2, large)}
      </EditableSlotGroup>
    );
  };

  return (
    <section className="section section--surface flex items-start justify-between gap-16 [&.section]:py-24 max-[1199px]:flex-col max-[1199px]:items-stretch max-[1199px]:gap-12 max-[1199px]:[&.section]:py-20 max-[767px]:gap-8 max-[767px]:[&.section]:py-16">
      <div className={`flex min-w-0 flex-col max-[1199px]:max-w-[640px] ${mode === 'none' ? 'max-w-[760px]' : 'flex-[0_1_560px]'}`} data-reveal>
        <div className="flex flex-col gap-4 pb-10 max-[1199px]:gap-3.5 max-[1199px]:pb-8 max-[767px]:gap-3 max-[767px]:pb-6">
          <RichTextContent path="lifestylehighlights.eyebrow" as="div" className="eyebrow" />
          <RichTextContent path="lifestylehighlights.title" as="h2" className="h2" />
          <RichTextContent
            path="lifestylehighlights.lead"
            as="p"
            className="m-0 text-lg leading-7 text-[--q-muted] max-[1199px]:text-[17px] max-[1199px]:leading-[26px] max-[767px]:text-base max-[767px]:leading-6"
          />
        </div>
        <EditableSlotGroup groupPath="lifestylehighlights.highlight_1" definition={highlight1Def} className={item}>
          <h3 className={itemTitle}>{h1.title}</h3>
          <p className={itemDesc}>{h1.description}</p>
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="lifestylehighlights.highlight_2" definition={highlight2Def} className={item}>
          <h3 className={itemTitle}>{h2.title}</h3>
          <p className={itemDesc}>{h2.description}</p>
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="lifestylehighlights.highlight_3" definition={highlight3Def} className={item}>
          <h3 className={itemTitle}>{h3.title}</h3>
          <p className={itemDesc}>{h3.description}</p>
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="lifestylehighlights.cta" definition={ctaDef} className="flex pt-8 empty:hidden max-[1199px]:pt-7 max-[767px]:pt-6">
          {showCta && (
            <a className="btn btn-primary" href={ctaHref} target={cta._target || '_self'}>
              {cta.label || 'See Every Amenity'} <span className="btn__arrow">→</span>
            </a>
          )}
        </EditableSlotGroup>
      </div>
      {mode === 'mosaic' && (
        <div className="flex min-w-0 flex-[0_1_592px] flex-col gap-6 max-[1199px]:flex-none max-[1199px]:gap-5 max-[767px]:gap-3" data-reveal style={stagger(1)}>
          {photoTile(0, true)}
          <div className="grid grid-cols-2 gap-6 max-[1199px]:gap-5 max-[767px]:gap-3">
            {photoTile(1, false)}
            {photoTile(2, false)}
          </div>
        </div>
      )}
      {mode === 'single' && (
        <div className="flex min-w-0 flex-[0_1_592px] flex-col max-[1199px]:flex-none" data-reveal style={stagger(1)}>
          {photoTile(singleIndex, true)}
        </div>
      )}
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'LifestyleHighlights',
  displayName: 'Lifestyle Highlights',
  tokens: {
    'lifestylehighlights.eyebrow': { label: 'Eyebrow', type: 'richtext', defaultValue: '<p>A lifestyle that fits you</p>' },
    'lifestylehighlights.title': { label: 'Title', type: 'richtext', defaultValue: '<p>Room for the way you live.</p>' },
    'lifestylehighlights.lead': {
      label: 'Lead',
      type: 'richtext',
      defaultValue:
        '<p>The amenities at <span data-type="field-token" data-field="property_name"></span> are built around how a week really goes: slow mornings by the pool, a cookout with the neighbors, and somewhere to work when the kitchen table is not cutting it.</p>',
      generator: {
        prompt: 'A lead on how the shared amenities fit into a resident’s week here. Name only amenities this property really has.',
        context: 'Lead of the Lifestyle Highlights section on the Why Quinn page, above three amenity highlights and a photo mosaic.',
        length: '1–2 sentences',
      },
    },
  },
  slotGroups: {
    'lifestylehighlights.highlight_1': highlight1Def,
    'lifestylehighlights.highlight_2': highlight2Def,
    'lifestylehighlights.highlight_3': highlight3Def,
    'lifestylehighlights.photo_1': photo1Def,
    'lifestylehighlights.photo_2': photo2Def,
    'lifestylehighlights.photo_3': photo3Def,
    'lifestylehighlights.cta': ctaDef,
  },
};

(LifestyleHighlights as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default LifestyleHighlights;
